// Settings overlay — client-only; persisted to localStorage.
// Opened via /settings command or the hotbar/HUD.
//
// The settings object exposes two families:
//   1. Per-surface UI toggles (hotbar/minimap/hud/account_widget/illustrations/…)
//      — any client code that renders a surface reads getSettings() and can
//      subscribe via onSettings().
//   2. A single MUD-only "master" preset that disables every visual surface
//      simultaneously, leaving the scrolling log + command input visible.
//      Flipping MUD-only writes the per-surface toggles too, so toggling it
//      off restores the user's previous visual layout.

export interface Settings {
  // Chat + behaviour
  chat_verbosity: "quiet" | "normal" | "all";
  auto_look_on_enter: boolean;
  confirm_quit: boolean;

  // Per-surface UI toggles
  show_hotbar: boolean;
  show_hud_bar: boolean;
  show_account_widget: boolean;
  show_minimap: boolean;
  show_exit_buttons: boolean;
  show_room_illustrations: boolean;
  show_portraits: boolean;      // dialogue portrait + NPC/mob chips
  show_item_icons: boolean;     // inventory + floor
  show_scene_3d: boolean;       // the Three.js backdrop

  // Master preset — flipping this writes the per-surface flags below.
  mud_only_mode: boolean;

  // ── Accessibility (HOO-197) ───────────────────────────────
  /** Colorblind palette override. */
  cb_mode: "none" | "deuteranopia" | "protanopia" | "tritanopia";
  /** High-contrast theme override. */
  contrast: "normal" | "high";
  /** Reduced-motion override (also picks up prefers-reduced-motion by default). */
  motion: "normal" | "reduced";
  /** Font family override for readability. */
  font: "normal" | "dyslexic";
  /** Text scale override. */
  text_scale: "normal" | "large" | "xlarge" | "xxlarge";
}

const DEFAULTS: Settings = {
  chat_verbosity: "normal",
  auto_look_on_enter: true,
  confirm_quit: false,
  show_hotbar: true,
  show_hud_bar: true,
  show_account_widget: true,
  show_minimap: true,
  show_exit_buttons: true,
  show_room_illustrations: true,
  show_portraits: true,
  show_item_icons: true,
  show_scene_3d: true,
  mud_only_mode: false,
  cb_mode: "none",
  contrast: "normal",
  motion: "normal",
  font: "normal",
  text_scale: "normal",
};

/** Apply accessibility settings to <body> data-* attributes; the CSS in
 *  index.html keys off these selectors to swap palettes, fonts, etc. */
export function applyA11yToBody(s: Settings): void {
  if (typeof document === "undefined") return;
  const body = document.body;
  if (!body) return;
  const set = (attr: string, val: string, defaultVal: string) => {
    if (val === defaultVal) body.removeAttribute(attr);
    else body.setAttribute(attr, val);
  };
  set("data-cb",         s.cb_mode,    "none");
  set("data-contrast",   s.contrast,   "normal");
  set("data-motion",     s.motion,     "normal");
  set("data-font",       s.font,       "normal");
  set("data-text-scale", s.text_scale, "normal");
}

// Aliases kept for backward compat — old localStorage used `minimap_visible`.
interface LegacyKeys { minimap_visible?: boolean }

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem("hoo_settings");
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Settings> & LegacyKeys;
    if (parsed.minimap_visible !== undefined && parsed.show_minimap === undefined) {
      parsed.show_minimap = parsed.minimap_visible;
    }
    return { ...DEFAULTS, ...parsed };
  } catch { return { ...DEFAULTS }; }
}

export function saveSettings(s: Settings): void {
  try { localStorage.setItem("hoo_settings", JSON.stringify(s)); } catch { /* */ }
}

type Listener = (s: Settings) => void;
const listeners = new Set<Listener>();
let current: Settings = loadSettings();

export function getSettings(): Settings { return current; }
export function onSettings(l: Listener): () => void { listeners.add(l); return () => listeners.delete(l); }
function emit(): void { for (const l of listeners) l(current); applyA11yToBody(current); }
// Apply a11y on first load so data-* attrs land before the first paint.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => applyA11yToBody(current));
  else applyA11yToBody(current);
}

export function updateSettings(patch: Partial<Settings>): void {
  current = { ...current, ...patch };
  saveSettings(current);
  emit();
}

/** Enter/exit MUD-only mode. Writes every per-surface toggle at once so that
 *  toggling back doesn't silently leave some surfaces hidden. The previous
 *  layout is captured so we can restore it on exit. */
let mudOnlyBackup: Partial<Settings> | null = null;
export function setMudOnlyMode(on: boolean): void {
  if (on) {
    mudOnlyBackup = {
      show_hotbar: current.show_hotbar,
      show_hud_bar: current.show_hud_bar,
      show_account_widget: current.show_account_widget,
      show_minimap: current.show_minimap,
      show_exit_buttons: current.show_exit_buttons,
      show_room_illustrations: current.show_room_illustrations,
      show_portraits: current.show_portraits,
      show_item_icons: current.show_item_icons,
      show_scene_3d: current.show_scene_3d,
    };
    updateSettings({
      mud_only_mode: true,
      show_hotbar: false, show_hud_bar: false, show_account_widget: false,
      show_minimap: false, show_exit_buttons: false,
      show_room_illustrations: false, show_portraits: false, show_item_icons: false,
      show_scene_3d: false,
    });
  } else {
    const restore = mudOnlyBackup ?? {
      show_hotbar: true, show_hud_bar: true, show_account_widget: true,
      show_minimap: true, show_exit_buttons: true,
      show_room_illustrations: true, show_portraits: true, show_item_icons: true,
      show_scene_3d: true,
    };
    mudOnlyBackup = null;
    updateSettings({ mud_only_mode: false, ...restore });
  }
}

const KEYBINDS: Array<[string, string]> = [
  ["look",           "L"],
  ["move N/S/E/W",   "W A S D / arrow keys"],
  ["diagonals",      "Q / E / Z / C"],
  ["chat focus",     "click input or type any letter"],
  ["global chat",    "/global <msg>  (or /g)"],
  ["faction chat",   "/faction <msg> (or /f)"],
  ["whisper",        "/whisper <name> <msg> (or /w)"],
  ["say (room)",     "/say or just type"],
  ["inventory",      "inv / i"],
  ["get item",       "get <name>  (click floor tags)"],
  ["talk NPC",       "talk <name>  (click NPC tag)"],
  ["attack mob",     "attack <name>"],
  ["settings",       "settings"],
  ["quit",           "quit / logout"],
];

type ToggleSpec = { key: keyof Settings; label: string; hint?: string };
const SURFACE_TOGGLES: ToggleSpec[] = [
  { key: "show_hotbar",             label: "Bottom hotbar",         hint: "Quick-access buttons (Cases, Casting, etc.)" },
  { key: "show_hud_bar",            label: "Top HUD buttons",       hint: "Character / Inventory / Quests / Skills / Help" },
  { key: "show_account_widget",     label: "Account widget",        hint: "Top-right name + tier + moon" },
  { key: "show_minimap",            label: "Mini-map",              hint: "Top-right zone layout" },
  { key: "show_exit_buttons",       label: "Exit buttons",          hint: "Clickable directions under room text" },
  { key: "show_room_illustrations", label: "Room illustrations",    hint: "Admin-attached artwork" },
  { key: "show_portraits",          label: "NPC + mob portraits",   hint: "Images in dialogue + hostile chips" },
  { key: "show_item_icons",         label: "Item icons",            hint: "Inventory + floor tags" },
  { key: "show_scene_3d",           label: "3D scene backdrop",     hint: "Three.js atmosphere behind the UI" },
];

/** Render the settings overlay into the given host. */
export function renderSettings(host: HTMLElement): void {
  const s = getSettings();
  host.innerHTML = `
    <div class="settings-modal">
      <h3>Settings <button id="set-close" class="set-close">×</button></h3>

      <section class="mud-only-section">
        <label class="mud-only-toggle">
          <input type="checkbox" id="set-mud-only" ${s.mud_only_mode ? "checked" : ""}/>
          <strong>MUD-only mode</strong>
          <span class="dim"> — hide every visual surface; keep log + input</span>
        </label>
      </section>

      <section>
        <h4>UI surfaces</h4>
        ${SURFACE_TOGGLES.map((t) => `
          <label>
            <input type="checkbox" data-sk="${t.key}" ${s[t.key] ? "checked" : ""} ${s.mud_only_mode ? "disabled" : ""}/>
            ${t.label}${t.hint ? ` <span class="dim">— ${t.hint}</span>` : ""}
          </label>
        `).join("")}
      </section>

      <section>
        <h4>Behaviour</h4>
        <label><input type="checkbox" id="set-autolook" ${s.auto_look_on_enter ? "checked" : ""}/> Auto-look on room entry</label>
        <label><input type="checkbox" id="set-confirm"  ${s.confirm_quit ? "checked" : ""}/> Confirm before /quit</label>
      </section>

      <section>
        <h4>Chat verbosity</h4>
        <label><input type="radio" name="cv" value="quiet"  ${s.chat_verbosity === "quiet"  ? "checked" : ""}/> Quiet (no system msgs)</label>
        <label><input type="radio" name="cv" value="normal" ${s.chat_verbosity === "normal" ? "checked" : ""}/> Normal</label>
        <label><input type="radio" name="cv" value="all"    ${s.chat_verbosity === "all"    ? "checked" : ""}/> All (verbose)</label>
      </section>

      <section>
        <h4>Keybinds &amp; commands</h4>
        <dl class="keybinds">
          ${KEYBINDS.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("")}
        </dl>
      </section>
    </div>
  `;
  host.querySelector<HTMLButtonElement>("#set-close")?.addEventListener("click", () => { host.innerHTML = ""; });
  host.querySelector<HTMLInputElement>("#set-mud-only")?.addEventListener("change", (e) => {
    setMudOnlyMode((e.target as HTMLInputElement).checked);
    renderSettings(host);
  });
  host.querySelectorAll<HTMLInputElement>("input[data-sk]").forEach((input) => {
    input.addEventListener("change", () => {
      const key = input.dataset.sk as keyof Settings;
      updateSettings({ [key]: input.checked } as unknown as Partial<Settings>);
    });
  });
  host.querySelector<HTMLInputElement>("#set-autolook")?.addEventListener("change", (e) =>
    updateSettings({ auto_look_on_enter: (e.target as HTMLInputElement).checked }));
  host.querySelector<HTMLInputElement>("#set-confirm")?.addEventListener("change", (e) =>
    updateSettings({ confirm_quit: (e.target as HTMLInputElement).checked }));
  host.querySelectorAll<HTMLInputElement>("input[name=cv]").forEach((r) => {
    r.addEventListener("change", () => {
      if (r.checked) updateSettings({ chat_verbosity: r.value as Settings["chat_verbosity"] });
    });
  });
}

export function closeSettings(host: HTMLElement): void { host.innerHTML = ""; }
